import { createClient, WebSocketBusClient } from "./ws-client";

// 为了类型友好，这里引入 tRPC 的类型与 observable 工具；
// 实际运行需要安装 @trpc/client 与 @trpc/server。
import type { AnyRouter } from "@trpc/server";
import type { TRPCLink } from "@trpc/client";
import { TRPCClientError } from "@trpc/client";
import { observable } from "@trpc/server/observable";
import superjson from "superjson";

export type JsonLike = unknown;

export type TrpcTransformer = {
  serialize: (value: unknown) => JsonLike;
  deserialize: (value: JsonLike) => unknown;
};

export const superjsonTransformer: TrpcTransformer = {
  serialize: (value: unknown) => superjson.serialize(value),
  // superjson 的类型定义需要这个断言
  deserialize: (value: JsonLike) => superjson.deserialize(value as any),
};

// 消息类型常量
const MESSAGE_KIND = {
  TRPC_REQUEST: "trpc",
  TRPC_RESULT: "trpc:result",
  TRPC_STOP: "trpc:stop",
} as const;

// 消息子类型常量
const MESSAGE_TYPE = {
  DIRECT: "direct",
} as const;

// 结果类型常量
const RESULT_TYPE = {
  DATA: "data",
  ERROR: "error",
  COMPLETE: "complete",
  STARTED: "started",
} as const;

// tRPC 错误常量
const TRPC_ERROR = {
  TIMEOUT: "TRPC_DIRECT_TIMEOUT",
  NOT_FOUND: "NOT_FOUND",
  SUBSCRIPTION_UNSUPPORTED: "SUBSCRIPTION_UNSUPPORTED",
} as const;

// 默认超时时间（仅对 query/mutation 生效）
const DEFAULT_TRPC_DIRECT_TIMEOUT_MS = 15_000;

export type TrpcDirectLinkOptions = {
  ws?: WebSocketBusClient;
  url?: string;
  peerId: string;
  token?: string;
  transformer?: TrpcTransformer;
  // undefined 使用默认值（15s）；null 或 0 可显式禁用超时
  timeoutMs?: number | null;
};

type TrpcDirectEnvelopeRequest = {
  kind: "trpc";
  rpcId: string;
  replyTo?: string;
  op: {
    type: "query" | "mutation" | "subscription";
    path: string;
    input: JsonLike;
  };
};

type TrpcDirectEnvelopeStop = {
  kind: "trpc:stop";
  rpcId: string;
};

type TrpcDirectEnvelopeResult =
  | { kind: "trpc:result"; rpcId: string; result: { type: "data"; data: JsonLike } }
  | { kind: "trpc:result"; rpcId: string; result: { type: "error"; error: JsonLike } }
  | { kind: "trpc:result"; rpcId: string; result: { type: "complete" } }
  | { kind: "trpc:result"; rpcId: string; result: { type: "started" } };

function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    "randomUUID" in crypto &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function identity<T>(v: T): T {
  return v;
}

export function createTrpcDirectLink<TRouter extends AnyRouter>(
  options: TrpcDirectLinkOptions
): TRPCLink<TRouter> {
  const transformer: TrpcTransformer = options.transformer ?? superjsonTransformer;

  const ws = options.ws ?? createClient({ url: options.url!, token: options.token });
  try {
    // 允许未 await 的预连接
    void ws.connect();
  } catch {}
  const effectiveTimeoutMs: number | null =
    options.timeoutMs === undefined ? DEFAULT_TRPC_DIRECT_TIMEOUT_MS : options.timeoutMs;

  return () => {
    return ({ op }) =>
      observable((observer) => {
        const rpcId = generateId();
        const peerId = options.peerId;
        let completed = false;
        let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

        const stopTimer = () => {
          if (timeoutHandle) {
            clearTimeout(timeoutHandle);
            timeoutHandle = null;
          }
        };

        const startTimer = () => {
          stopTimer();
          const ms = effectiveTimeoutMs;
          if (typeof ms === "number" && ms > 0 && op.type !== "subscription") {
            timeoutHandle = setTimeout(() => {
              if (completed) return;
              completed = true;
              // TRPCClientError 类型需要这个断言以匹配 observer.error 的签名
              observer.error(
                TRPCClientError.from(new Error(TRPC_ERROR.TIMEOUT)) as any
              );
            }, ms);
          }
        };

        const off = ws.onType(MESSAGE_TYPE.DIRECT, (msg) => {
          const from = (msg as Record<string, unknown>).from;
          if (from !== peerId) return;
          const payload: unknown = (msg as Record<string, unknown>).payload;
          if (!payload || typeof payload !== "object") return;
          if ((payload as Record<string, unknown>).kind !== MESSAGE_KIND.TRPC_RESULT) return;
          const res = payload as TrpcDirectEnvelopeResult;
          if (res.rpcId !== rpcId) return;
          if (completed) return;

          if (res.result.type === RESULT_TYPE.DATA) {
            stopTimer();
            observer.next({
              result: {
                type: RESULT_TYPE.DATA,
                data: transformer.deserialize(res.result.data),
              },
            });
            if (op.type !== "subscription") {
              completed = true;
              observer.complete();
            }
            return;
          }
          if (res.result.type === RESULT_TYPE.STARTED) {
            // 订阅已建立的握手事件
            observer.next({ result: { type: RESULT_TYPE.STARTED } });
            return;
          }
          if (res.result.type === RESULT_TYPE.ERROR) {
            stopTimer();
            completed = true;
            const errValue = transformer.deserialize(res.result.error);
            observer.error(
              TRPCClientError.from(
                errValue instanceof Error ? errValue : new Error(String(errValue))
              )
            );
            return;
          }
          if (res.result.type === RESULT_TYPE.COMPLETE) {
            stopTimer();
            completed = true;
            observer.complete();
            return;
          }
        });

        startTimer();

        ws.sendDirect(peerId, {
          kind: MESSAGE_KIND.TRPC_REQUEST,
          rpcId,
          op: {
            type: op.type,
            path: op.path,
            input: transformer.serialize(op.input),
          },
        } satisfies TrpcDirectEnvelopeRequest);

        return () => {
          off();
          stopTimer();
          if (!completed && op.type === "subscription") {
            ws.sendDirect(peerId, { kind: MESSAGE_KIND.TRPC_STOP, rpcId } satisfies TrpcDirectEnvelopeStop);
          }
        };
      });
  };
}

export type TrpcDirectHandlerOptions<TRouter extends AnyRouter, TContext> = {
  ws: WebSocketBusClient;
  router: TRouter;
  getContext: (meta: { from: string }) => Promise<TContext> | TContext;
  transformer?: TrpcTransformer;
  onError?: (meta: { from: string; rpcId: string; error: unknown }) => void;
};

function isAsyncIterable<T>(value: unknown): value is AsyncIterable<T> {
  return (
    value != null &&
    typeof value === "object" &&
    Symbol.asyncIterator in value &&
    typeof (value as Record<symbol, unknown>)[Symbol.asyncIterator] === "function"
  );
}

function isSubscribable<T>(value: unknown): value is { subscribe: Function } {
  return (
    value != null &&
    typeof value === "object" &&
    "subscribe" in value &&
    typeof (value as Record<string, unknown>).subscribe === "function"
  );
}

// 辅助函数：通过路径获取 procedure（改进类型安全性）
function getProcedure(caller: Record<string, any>, path: string): unknown {
  const parts = path.split(".").filter(Boolean);
  let current: any = caller;
  for (const key of parts) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
}

// 辅助函数：从 payload 中提取 replyTo 地址
function getReplyTo(payload: unknown, from: string | undefined): string {
  const payloadObj = payload as Record<string, unknown>;
  const replyToValue = payloadObj.replyTo;
  return typeof replyToValue === "string" && replyToValue.length > 0
    ? replyToValue
    : (from ?? "unknown");
}

// 辅助函数：发送错误响应
function sendErrorResult(
  ws: WebSocketBusClient,
  replyTo: string,
  rpcId: string,
  error: unknown,
  transformer: TrpcTransformer
): void {
  ws.sendDirect(replyTo, {
    kind: MESSAGE_KIND.TRPC_RESULT,
    rpcId,
    result: { type: RESULT_TYPE.ERROR, error: transformer.serialize(error) },
  } as TrpcDirectEnvelopeResult);
}

// 辅助函数：发送数据响应
function sendDataResult(
  ws: WebSocketBusClient,
  replyTo: string,
  rpcId: string,
  data: unknown,
  transformer: TrpcTransformer
): void {
  ws.sendDirect(replyTo, {
    kind: MESSAGE_KIND.TRPC_RESULT,
    rpcId,
    result: { type: RESULT_TYPE.DATA, data: transformer.serialize(data) },
  } as TrpcDirectEnvelopeResult);
}

// 辅助函数：发送完成响应
function sendCompleteResult(
  ws: WebSocketBusClient,
  replyTo: string,
  rpcId: string
): void {
  ws.sendDirect(replyTo, {
    kind: MESSAGE_KIND.TRPC_RESULT,
    rpcId,
    result: { type: RESULT_TYPE.COMPLETE },
  } as TrpcDirectEnvelopeResult);
}

// 辅助函数：发送已启动响应
function sendStartedResult(
  ws: WebSocketBusClient,
  replyTo: string,
  rpcId: string
): void {
  ws.sendDirect(replyTo, {
    kind: MESSAGE_KIND.TRPC_RESULT,
    rpcId,
    result: { type: RESULT_TYPE.STARTED },
  } as TrpcDirectEnvelopeResult);
}

export function attachTrpcDirectHandler<TRouter extends AnyRouter, TContext>(
  options: TrpcDirectHandlerOptions<TRouter, TContext>
): () => void {
  const transformer: TrpcTransformer = options.transformer ?? superjsonTransformer;

  const activeSubscriptions = new Map<
    string,
    { unsubscribe: () => void; replyTo: string; peer: string }
  >();

  const off = options.ws.onType(MESSAGE_TYPE.DIRECT, async (msg) => {
    const from = (msg as Record<string, unknown>).from as string | undefined;
    const payload = (msg as Record<string, unknown>).payload;
    if (!payload || typeof payload !== "object") return;
    const kind = (payload as Record<string, unknown>).kind as string | undefined;
    if (kind !== MESSAGE_KIND.TRPC_REQUEST && kind !== MESSAGE_KIND.TRPC_STOP) return;

    if (kind === MESSAGE_KIND.TRPC_STOP) {
      const rpcId = String((payload as Record<string, unknown>).rpcId ?? "");
      const sub = activeSubscriptions.get(rpcId);
      if (sub) {
        try {
          sub.unsubscribe();
        } catch {}
        activeSubscriptions.delete(rpcId);
        sendCompleteResult(options.ws, sub.replyTo, rpcId);
      }
      return;
    }

    const payloadObj = payload as Record<string, unknown>;
    const rpcId = String(payloadObj.rpcId ?? "");
    const op = payloadObj.op as TrpcDirectEnvelopeRequest["op"];
    const replyTo = getReplyTo(payload, from);

    try {
      const ctx = await options.getContext({ from: from ?? "unknown" });
      // tRPC router 的 createCaller 是动态方法，需要这个断言
      const caller = (options.router as any).createCaller(ctx);
      const proc = getProcedure(caller, op.path);
      if (typeof proc !== "function") {
        sendErrorResult(options.ws, replyTo, rpcId, TRPC_ERROR.NOT_FOUND, transformer);
        return;
      }

      if (op.type === "query" || op.type === "mutation") {
        const output = await proc(transformer.deserialize(op.input));
        sendDataResult(options.ws, replyTo, rpcId, output, transformer);
        return;
      }

      if (op.type === "subscription") {
        const value = await proc(transformer.deserialize(op.input));
        if (isAsyncIterable<any>(value)) {
          const controller = new AbortController();
          activeSubscriptions.set(rpcId, {
            replyTo,
            peer: from ?? "unknown",
            unsubscribe: () => controller.abort(),
          });
          // 通知客户端订阅已建立
          sendStartedResult(options.ws, replyTo, rpcId);
          (async () => {
            try {
              for await (const item of value) {
                if (controller.signal.aborted) break;
                sendDataResult(options.ws, replyTo, rpcId, item, transformer);
              }
            } catch (e) {
              sendErrorResult(options.ws, replyTo, rpcId, e, transformer);
              return;
            }
            sendCompleteResult(options.ws, replyTo, rpcId);
          })();
          return;
        }
        if (isSubscribable<any>(value)) {
          // Observable 的 subscribe 方法签名可能不完全匹配，需要断言
          const subscription = (value as any).subscribe(
            (item: any) => {
              sendDataResult(options.ws, replyTo, rpcId, item, transformer);
            },
            (e: any) => {
              sendErrorResult(options.ws, replyTo, rpcId, e, transformer);
            },
            () => {
              sendCompleteResult(options.ws, replyTo, rpcId);
            }
          );
          activeSubscriptions.set(rpcId, {
            replyTo,
            peer: from ?? "unknown",
            unsubscribe: () => {
              try {
                subscription?.unsubscribe?.();
              } catch {}
            },
          });
          // 通知客户端订阅已建立
          sendStartedResult(options.ws, replyTo, rpcId);
          return;
        }
        sendErrorResult(options.ws, replyTo, rpcId, TRPC_ERROR.SUBSCRIPTION_UNSUPPORTED, transformer);
        return;
      }
    } catch (error) {
      try {
        options.onError?.({ from: from ?? "unknown", rpcId, error });
      } catch {}
      try {
        const replyTo = getReplyTo(payload, from);
        sendErrorResult(options.ws, replyTo, rpcId, error, transformer);
      } catch {}
    }
  });

  return () => {
    try {
      off();
    } catch {}
    for (const sub of Array.from(activeSubscriptions.values())) {
      try {
        sub.unsubscribe();
      } catch {}
    }
    activeSubscriptions.clear();
  };
}

/**
最小用法示例：

// A 客户端 —— 暴露 tRPC Router
import type { AppRouter } from "../path/to/router";
const aWs = createClient({ url: "wss://bus", token: "..." });
await aWs.connect();
const detach = attachTrpcDirectHandler<AppRouter, Context>({
  ws: aWs,
  router: appRouter,
  getContext: () => ({ /* ... *\/ }),
});

// B 客户端 —— 作为 tRPC Client 远程调用 A
import { createTRPCProxyClient } from "@trpc/client";
const bWs = createClient({ url: "wss://bus", token: "..." });
await bWs.connect();
const client = createTRPCProxyClient<AppRouter>({
  links: [createTrpcDirectLink<AppRouter>({ ws: bWs, peerId: "<A 的 clientId>" })],
});
const data = await client.user.get.query({ id: "123" });
*/


